// routes/admin/AdminPushDashboard.tsx
import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { useUserProfile } from "../../contexts/UserProfileContext";

type TokenRow = {
  id: string;
  user_id: string | null;
  token: string;
  platform: string | null;
  device_info: string | null;
  updated_at: string | null;
  profiles: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    push_for_tasks: boolean | null;
  } | null;
};

export default function AdminPushDashboard() {
  const { profile } = useUserProfile();
  const [tokens, setTokens] = useState<TokenRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile || profile.role !== "admin") return;

    const fetchTokens = async () => {
      const { data, error } = await supabase
        .from("user_push_tokens")
        .select("*, profiles(first_name, last_name, email, push_for_tasks)")
        .order("updated_at", { ascending: false });

      if (error) {
        console.error("Fehler beim Laden der Push-Tokens:", error);
      } else {
        setTokens(data);
      }

      setLoading(false);
    };

    fetchTokens();
  }, [profile]);

  const handleDeleteToken = async (id: string) => {
    const { error } = await supabase
      .from("user_push_tokens")
      .delete()
      .eq("id", id);
    if (error) {
      console.error("❌ Fehler beim Löschen:", error);
      return;
    }
    setTokens((prev) => prev.filter((token) => token.id !== id));
  };

  if (!profile || profile.role !== "admin") {
    return (
      <div className="text-center text-red-400 mt-10">
        Kein Zugriff – nur für Admins 🛑
      </div>
    );
  }

  const grouped = tokens.reduce((acc, entry) => {
    const uid = entry.user_id;
    if (!uid) return acc;
    if (!acc[uid]) acc[uid] = [];
    acc[uid].push(entry);
    return acc;
  }, {} as Record<string, TokenRow[]>);

  return (
    <div className="max-w-5xl mx-auto mt-10 p-6">
      <h1 className="text-2xl font-bold text-sky-400 mb-4">
        📬 Push-Dashboard
      </h1>
      <p className="text-slate-400 mb-6">
        Übersicht aller gespeicherten Push-Tokens, gruppiert nach Nutzern.
      </p>

      {loading ? (
        <p className="text-slate-400">Lade Daten …</p>
      ) : (
        Object.entries(grouped).map(([userId, userTokens]) => {
          const profile = userTokens[0]?.profiles;
          const name = profile
            ? `${profile.first_name ?? ""} ${profile.last_name ?? ""}`.trim() || "🧍 Unbekannter Name"
            : "🧍 Unbekannter Name";

          return (
            <div
              key={userId}
              className="mb-6 border border-[#2a3347] rounded-xl bg-[#161b27]"
            >
              <div className="p-4 border-b border-[#2a3347] flex justify-between items-center">
                <div>
                  <h2 className="font-bold text-lg text-slate-100">{name}</h2>
                  <p className="text-sm text-slate-500">{profile?.email}</p>
                </div>
                <div className="text-sm text-slate-400">
                  Push erlaubt: {profile?.push_for_tasks ? "✅" : "❌"}
                </div>
              </div>

              <div className="divide-y divide-[#2a3347]">
                {userTokens.map((token) => (
                  <div
                    key={token.id}
                    className="p-4 flex justify-between items-start gap-4"
                  >
                    <div className="text-sm text-slate-300">
                      <div>
                        <strong>Plattform:</strong> {token.platform}
                      </div>
                      <div>
                        <strong>Gerät:</strong> {token.device_info}
                      </div>
                      <div>
                        <strong>Zuletzt aktualisiert:</strong>{" "}
                        {new Date(token.updated_at ?? "").toLocaleString()}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteToken(token.id)}
                      className="text-sm text-red-400 hover:text-red-300 transition-colors"
                    >
                      🗑️ Löschen
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
